/**
 * services/authApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Authentication and user session methods.
 *
 * METHODS: login, loginWithPassword, sendVerificationCode, verifyCode,
 *   signup, logout, getProfile, updateProfile, checkAdminStatus,
 *   checkUserExists  ← NEW
 *
 * HOW IT WORKS:
 *   This file exports a `methods` object. The main api.js spreads it into
 *   the ApiService class, so callers use: apiService.login(...)
 *   You can also import directly: import { methods as authApi } from './authApi'
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {

  /**
   * GET /api/auth/check-user/?email=<email>
   *
   * Checks whether a live UserProfile exists for the given email.
   * Used by LoginModal to decide whether to show the login form or
   * auto-switch to Sign Up when no account is found.
   *
   * Returns: { exists: true | false }
   *
   * ✅ This checks UserProfile ONLY — not the auth User table — so deleted
   *    users (who still have orphan auth rows) are treated as non-existent,
   *    allowing them to re-register cleanly with referral codes applied.
   */
  async checkUserExists(email) {
    try {
      const res = await fetch(
        `${API_BASE_URL}/auth/check-user/?email=${encodeURIComponent(email.trim().toLowerCase())}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return { exists: false };
      return await res.json(); // { exists: true | false }
    } catch {
      // On network error, default to "exists" so we don't wrongly push someone to signup
      return { exists: true };
    }
  },

  async login(credentials) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      return await res.json();
    } catch (error) { console.error('Login error:', error); throw error; }
  },

  async loginWithPassword(identifier, password) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
      });
      // ✅ Return full response data including redirect_to_signup flag from backend
      return await res.json();
    } catch { return { success: false, error: 'Network error. Please check your connection and try again.' }; }
  },

  async sendVerificationCode(identifier) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      return await res.json();
    } catch { return { success: false, error: 'Network error. Please check your connection and try again.' }; }
  },

  async verifyCode(identifier, code) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, code }),
      });
      return await res.json();
    } catch { return { success: false, error: 'Network error. Please try again.' }; }
  },

  async signup(userData) {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/signup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      return await res.json();
    } catch { return { success: false, error: 'Network error. Please try again.' }; }
  },

  async logout() {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/logout/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
      });
      return await res.json();
    } catch (error) { console.error('Logout error:', error); throw error; }
  },

  async getProfile() {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/profile/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      return await res.json();
    } catch (error) { console.error('Get profile error:', error); throw error; }
  },

  async updateProfile(profileData) {
    try {
      const token = this._getToken();
      const res = await fetch(`${API_BASE_URL}/auth/profile/`, {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}` },
        body: profileData,
      });
      return await res.json();
    } catch (error) { console.error('Update profile error:', error); throw error; }
  },

  async checkAdminStatus(token) {
    try {
      const t = token || this._getToken();
      const res = await fetch(`${API_BASE_URL}/admin/check-status/`, {
        headers: { 'Authorization': `Token ${t}` },
      });
      if (!res.ok) throw new Error('Failed to check admin status');
      return await res.json();
    } catch { return { is_admin: false }; }
  }

  // ADS / PRODUCTS
  // ✅ fetchAds: follows Django pagination `next` URL in a loop until null.
  //    No hardcoded page_size — fetches every single ad regardless of count.
};

